import { HoverCard as HoverCardPrimitive } from 'radix-ui';
import React from 'react';
import { cn } from '@core/lib/utils';
import { ImageOff, Loader2, Expand } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@core/components/ui/shared/dialogs/dialog';

/**
 * Props for {@link LinkPreview}.
 */
type LinkPreviewProps = {
  children: React.ReactNode;
  url: string;
  className?: string;
  width?: number;
  height?: number;
  quality?: number;
  layout?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  redirect?: boolean;
} & (
  | { isStatic: true; imageSrc: string }
  | { isStatic?: false; imageSrc?: never }
);

/**
 * Shadcn-only fallback for the motion {@link LinkPreview}.
 *
 * Replaces `motion.div` spring-scale entrance with Radix HoverCard
 * `data-[state]:animate-in/out` classes. Removes `useMotionValue`/`useSpring`
 * translateX drift — the preview panel is statically centered. Image loading,
 * canvas-based screenshot caching, click-to-expand dialog, and redirect mode
 * are all retained unchanged.
 *
 * Uses `preserveState` Activity dual-mount so `cachedImageSrc` and
 * `isImageLoading` survive a perf-mode toggle mid-load.
 *
 * @param props - Same public API as the motion variant.
 */
const LinkPreview = ({
  children,
  url,
  className,
  width = 200,
  height = 125,
  quality = 50,
  layout = 'fixed',
  isStatic = false,
  imageSrc = '',
  side = 'left',
  redirect = false,
}: LinkPreviewProps) => {
  const [isOpen, setOpen] = React.useState(false);
  const [isMounted, setIsMounted] = React.useState(false);
  const [isImageLoading, setIsImageLoading] = React.useState(true);
  const [cachedImageSrc, setCachedImageSrc] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  React.useEffect(() => {
    if (isOpen && !cachedImageSrc && !isStatic) {
      setIsImageLoading(true);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL();
            if (!redirect) setCachedImageSrc(dataUrl);
            setIsImageLoading(false);
          }
        } catch {
          if (!redirect) setCachedImageSrc(url);
          setIsImageLoading(false);
        }
      };
      img.onerror = () => setIsImageLoading(false);
      img.src = url;
    } else if (isOpen && cachedImageSrc) {
      setIsImageLoading(false);
    }
  }, [isOpen, url, cachedImageSrc, isStatic, width, height, redirect]);

  const displaySrc = isStatic ? imageSrc : cachedImageSrc || url;

  return (
    <>
      <HoverCardPrimitive.Root
        openDelay={50}
        closeDelay={100}
        onOpenChange={(open) => setOpen(open)}
      >
        <HoverCardPrimitive.Trigger
          data-slot="link-preview-trigger"
          className={cn('text-black dark:text-white', className)}
        >
          {children}
        </HoverCardPrimitive.Trigger>

        <HoverCardPrimitive.Content
          data-slot="link-preview-content"
          className="origin-(--radix-hover-card-content-transform-origin) data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 duration-150"
          side={side}
          align="center"
          sideOffset={10}
        >
          <div
            data-slot="link-preview-card"
            className={cn(
              'shadow-xl perf-no-shadows:shadow-none rounded-xl z-50',
              !redirect && 'cursor-pointer',
            )}
            onClick={!redirect ? () => setDialogOpen(true) : undefined}
          >
            {redirect ? (
              <a
                href={url}
                className="block p-1 bg-white border-2 border-transparent shadow perf-no-shadows:shadow-none rounded-xl hover:border-neutral-200 dark:hover:border-neutral-800"
                style={{ fontSize: 0 }}
              >
                {isImageLoading && (
                  <div
                    className="flex items-center justify-center rounded-lg bg-muted"
                    style={{ width: `${width}px`, height: `${height}px` }}
                  >
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                )}
                <img
                  src={displaySrc.trim()?.length > 0 ? displaySrc : undefined}
                  width={width}
                  height={height}
                  className={cn(
                    'rounded-lg',
                    (isImageLoading || url.trim()?.length <= 0) && 'hidden',
                  )}
                  alt="preview image"
                  data-embed="screenshot.url"
                  data-color-scheme="dark"
                  data-viewport-is-mobile="true"
                  data-viewport-device-scale-factor="1"
                  data-viewport-width={width * 3}
                  data-viewport-height={height * 3}
                  style={{ width: `${width}px`, height: `${height}px`, objectFit: 'cover' }}
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => setIsImageLoading(false)}
                />
                {!isImageLoading && url.trim()?.length <= 0 && (
                  <div
                    className="flex items-center justify-center rounded-lg bg-muted"
                    style={{ width, height }}
                  >
                    <ImageOff />
                  </div>
                )}
              </a>
            ) : (
              <div
                className="relative block p-1 bg-white border-2 border-transparent shadow perf-no-shadows:shadow-none rounded-xl hover:border-neutral-200 dark:hover:border-neutral-800"
                style={{ fontSize: 0 }}
              >
                {isImageLoading && (
                  <div
                    className="flex items-center justify-center rounded-lg bg-muted"
                    style={{ width: `${width}px`, height: `${height}px` }}
                  >
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                )}
                <img
                  src={displaySrc.trim()?.length > 0 ? displaySrc : undefined}
                  width={width}
                  height={height}
                  className={cn(
                    'rounded-lg',
                    (isImageLoading || url.trim()?.length <= 0) && 'hidden',
                  )}
                  alt="preview image"
                  data-embed="screenshot.url"
                  data-color-scheme="dark"
                  data-viewport-is-mobile="true"
                  data-viewport-device-scale-factor="1"
                  data-viewport-width={width * 3}
                  data-viewport-height={height * 3}
                  style={{ width: `${width}px`, height: `${height}px`, objectFit: 'cover' }}
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => setIsImageLoading(false)}
                />
                {!isImageLoading && url.trim()?.length <= 0 && (
                  <div
                    className="flex items-center justify-center rounded-lg bg-muted"
                    style={{ width, height }}
                  >
                    <ImageOff />
                  </div>
                )}
                {!isImageLoading && displaySrc.trim()?.length > 0 && (
                  <div className="absolute bottom-1 right-1 opacity-50 hover:opacity-100 transition-opacity perf-no-transitions:transition-none duration-200 bg-black/50 rounded p-1">
                    <Expand className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
            )}
          </div>
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Root>

      {!redirect && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTitle className="hidden" />
          <DialogContent className="max-w-md" aria-describedby={undefined}>
            <div className="flex justify-center">
              {isImageLoading ? (
                <div
                  className="flex items-center justify-center bg-muted rounded-lg"
                  style={{ width: '400px', height: '300px' }}
                >
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : url.trim()?.length > 0 ? (
                <img
                  src={url}
                  alt="Preview"
                  className="rounded-lg max-w-full max-h-full"
                  style={{ objectFit: 'contain' }}
                />
              ) : (
                <div
                  className="flex items-center justify-center bg-muted rounded-lg"
                  style={{ width: '400px', height: '300px' }}
                >
                  <ImageOff className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default LinkPreview;
